import { actions } from "@mixitone/actions";
import {
  Account,
  TournamentCompetition,
  TournamentCompetitionConflict,
  TournamentCompetitionEvent,
  TournamentEntry,
  type TournamentEntryDragData,
  TournamentMatch,
  TournamentPaymentMethod,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { observe } from "@mixitone/mvc";
import { debounceScheduler } from "@mixitone/mvc/src/utils/scheduler";
import { List, Model, setupModelRelations, subscribeList } from "@mixitone/oom";
import { SubscriptionManager } from "@mixitone/oom/SubscriptionManager";
import { debounce, isPresent, withBatch, withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import { AppController } from "controllers/AppController";
import MixitoneController from "controllers/MixitoneController";
import { messagesService } from "services";
import { agentToolsService, resource } from "services/AgentToolsService";
import routeService from "services/RouteService";
import { type FormatName, TournamentStateConverter, formats, getFormat } from "tournament-formats";
import type { SchemaField } from "tournament-formats/TournamentFormat";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import { planAutomaticNoShowVoids } from "tournament-state/automaticVoidPlanner";
import { tournamentEventStreamToken } from "tournament-state/tournamentEventStreamToken";
import { getMatchSlotId } from "tournament-state/TournamentProjection";
import {
  roundRobinGroupsLocked,
  roundRobinHasGroups,
  setRoundRobinGroupsLocked,
  syncEntryGroupToCompetition,
} from "./tournamentHelpers";

interface TournamentMatchesResetEvent {
  competition: TournamentCompetition;
}

interface State {
  id: string;
  loading: boolean;
  generatingMatches: boolean;
  cleaningUpMatches?: boolean;
  tournamentSession: TournamentSession;
  players: List<TournamentPlayer>;
  registrations: List<TournamentRegistration>;
  competitions: List<TournamentCompetition>;
  paymentMethods: List<TournamentPaymentMethod>;
  entries: List<TournamentEntry>;
  matches: List<TournamentMatch>;
  matchEntries: Record<string, string[]>;
  showCompetitionCompletedModal: Record<string, boolean>;
  showCompleteTournamentModal: boolean;
  completingTournament: boolean;
  showThankYouModal: boolean;
  thankYouText: string;
  sendingThankYou: boolean;
}

interface Props {
  params: { id: string };
}

export interface TournamentOpenConflict {
  id?: string;
  conflict_key?: string;
  conflict_type?: string;
  event_ids?: string[];
  payload?: Record<string, unknown>;
  status?: string;
}

class TournamentController extends MixitoneController<State, Props> {
  private readonly generationSubscriptionSources: Array<"client" | "server" | "sync"> = [
    "client",
    "server",
    "sync",
  ];
  private projectionFingerprint(matches: TournamentMatch[]): string {
    return matches
      .map((match) => {
        const current = match.entries?.current || [];
        const winners = match.entries?.winners || [];
        const losers = match.entries?.losers || [];
        const score = match.entries?.score || {};
        const scoreEntries = Object.entries(score)
          .map(([entryId, value]) => `${entryId}:${String(value)}`)
          .toSorted()
          .join("|");
        return [
          match.id || "",
          match.status || "",
          match.court_id || "",
          current.join(","),
          winners.join(","),
          losers.join(","),
          scoreEntries,
        ].join("::");
      })
      .toSorted()
      .join("||");
  }

  private normalizeGroupsMetadata(value: unknown): Record<string, string[]> {
    if (!value || typeof value !== "object") return {};
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([groupName, entryIds]) => {
        if (!Array.isArray(entryIds)) return [groupName, []] as const;
        const normalizedIds = entryIds
          .filter((entryId): entryId is string => typeof entryId === "string")
          .toSorted();
        return [groupName, normalizedIds] as const;
      })
      .filter(([, entryIds]) => entryIds.length > 0)
      .toSorted(([a], [b]) => a.localeCompare(b));
    return Object.fromEntries(entries);
  }

  private async persistRoundRobinGroups(
    competition: TournamentCompetition,
    state: {
      entries?: Array<[string, number, Record<string, unknown>?]>;
      metadata?: Record<string, unknown>;
    },
  ) {
    if (competition.format !== "RoundRobin") return;

    const groupsFromState = this.normalizeGroupsMetadata(state.metadata?.groups);
    const groupsFromCompetition = this.normalizeGroupsMetadata(competition.format_data?.groups);
    if (JSON.stringify(groupsFromState) !== JSON.stringify(groupsFromCompetition)) {
      const nextFormatData: Record<string, unknown> = {
        ...(competition.format_data || {}),
        groups: groupsFromState,
      };
      if (typeof state.metadata?.groupsLocked === "boolean") {
        nextFormatData.groupsLocked = state.metadata.groupsLocked;
      }
      competition.format_data = nextFormatData;
      await competition.save();
    }

    const nextGroupByEntryId = new Map<string, string | undefined>();
    for (const entry of state.entries || []) {
      const entryId = entry?.[0];
      const metadata = entry?.[2];
      if (typeof entryId !== "string") continue;
      const group =
        typeof metadata?.group === "string" && metadata.group.length > 0 ? metadata.group : undefined;
      nextGroupByEntryId.set(entryId, group);
    }

    const entriesForCompetition = this.state.entries.filter(
      (entry) => entry.tournament_competition_id === competition.id,
    );
    for (const entry of entriesForCompetition) {
      const nextGroup = nextGroupByEntryId.get(entry.id!) || undefined;
      if (entry.group === nextGroup) continue;
      console.log("[GroupWrite][TournamentController.generateCompetitionMatches.persistRoundRobinGroups]", {
        entryId: entry.id,
        competitionId: competition.id,
        previousGroup: entry.group,
        nextGroup,
      });
      entry.group = nextGroup;
      await entry.save();
    }
  }

  private async persistRoundRobinDivisions(
    competition: TournamentCompetition,
    state: {
      entries?: Array<[string, number, Record<string, unknown>?]>;
    },
  ) {
    if (competition.format !== "RoundRobin") return false;
    const formatSettings = competition.format_settings as
      | { enablePlayoffs?: boolean; divisions?: number }
      | undefined;
    if (formatSettings?.enablePlayoffs !== true) return false;
    const numDivisions = formatSettings.divisions ?? 1;
    if (numDivisions <= 1) return false;

    const nextDivisionByEntryId = new Map<string, number | undefined>();
    for (const entry of state.entries || []) {
      const entryId = entry?.[0];
      if (typeof entryId !== "string") continue;
      const raw = entry?.[2]?.division;
      const value = typeof raw === "number" && raw >= 1 && raw <= numDivisions ? raw : undefined;
      nextDivisionByEntryId.set(entryId, value);
    }

    const normalizeDivision = (value: number | null | undefined) =>
      typeof value === "number" && value >= 1 && value <= numDivisions ? value : undefined;

    const entriesForCompetition = this.state.entries.filter(
      (entry) => entry.tournament_competition_id === competition.id,
    );
    let needsRegenerate = false;
    for (const entry of entriesForCompetition) {
      const nextDivision = nextDivisionByEntryId.get(entry.id!);
      const liveDivision = normalizeDivision(entry.division);
      if (liveDivision === nextDivision) continue;
      // Unsaved local format_data (e.g. an open entry form) is newer than this
      // reduce. Leave it and re-run generation after it saves.
      const hasUnsavedFormatData =
        Boolean(entry.changedAttributes?.has?.("format_data")) ||
        Boolean(entry.pendingAttributes?.has?.("format_data"));
      if (hasUnsavedFormatData) {
        needsRegenerate = true;
        continue;
      }
      entry.division = nextDivision;
      await entry.save();
    }
    return needsRegenerate;
  }

  async waitForGenerationIdle(timeoutMs = 30_000) {
    const deadline = Date.now() + timeoutMs;
    while (this.state.generatingMatches && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async actionGenerateCompetitionNow(competitionId: string) {
    await this.waitForGenerationIdle();
    this.competitionsRequiringGeneration.add(competitionId);
    await this.generateMatches();
    await this.waitForGenerationIdle();
    if (this.competitionsRequiringGeneration.has(competitionId)) {
      await this.generateMatches();
      await this.waitForGenerationIdle();
    }
    await this.replaceCompetitionMatchesFromProjection(competitionId);
  }

  async actionSetPlayoffDivision(competitionId: string, entryId: string, division: number | undefined) {
    const entry = this.state.entries.findById(entryId);
    const competition = this.state.competitions.findById(competitionId);
    if (!entry || !competition) return;
    const playoffMatches = this.state.matches.filter(
      (match) => match.tournament_competition_id === competitionId && match.format_data?.phase === "playoff",
    );
    if (playoffMatches.some((match) => match.isInProgress || match.isComplete || match.isVoided)) {
      messagesService.addMessage(
        "Playoff divisions cannot be changed after playoff matches have started",
        "error",
      );
      return;
    }
    await new TournamentDataAdapter().appendEvent({
      competition,
      eventType: "playoff_division_set",
      payload: { entry_id: entryId, division: division ?? null },
    });
    await this.actionGenerateCompetitionNow(competitionId);
  }

  private async replaceCompetitionMatchesFromProjection(competitionId: string) {
    const competition = this.state.competitions.findById(competitionId);
    if (!competition) return;
    const adapter = new TournamentDataAdapter();
    const projectedMatches = await adapter.loadProjectedMatches(competition);
    const unaffectedMatches = this.state.matches.filter(
      (match) => match.tournament_competition_id !== competitionId,
    );
    this.state.matches = new List(...unaffectedMatches, ...projectedMatches);
  }

  get initialState(): State {
    return {
      id: "",
      loading: false,
      generatingMatches: false,
      tournamentSession: new TournamentSession({}),
      players: new List(),
      registrations: new List(),
      competitions: new List(),
      paymentMethods: new List(),
      entries: new List(),
      matches: new List(),
      matchEntries: {},
      showCompetitionCompletedModal: {},
      showCompleteTournamentModal: false,
      completingTournament: false,
      showThankYouModal: false,
      thankYouText: "",
      sendingThankYou: false,
    };
  }

  override async initialize(props: Props) {
    this.registerAgentResources();
    await this.changeProps(props);

    const abortController = new AbortController();
    window.addEventListener(
      "queueGeneration",
      (event) => {
        const detail = (event as any).detail as { competitionId: string; immediate?: boolean };
        if (detail.immediate) {
          this.queueCompetitionForGenerationImmediate(detail.competitionId);
          return;
        }
        this.queueCompetitionForGeneration(detail.competitionId);
      },
      { signal: abortController.signal },
    );
    this.addDependency(() => abortController.abort());

    const regenerateGroupsAbortController = new AbortController();
    window.addEventListener(
      "regenerateGroups",
      (event) => {
        const detail = (event as any).detail as { competitionId: string };
        this.actionRegenerateGroups(detail.competitionId);
      },
      { signal: regenerateGroupsAbortController.signal },
    );
    this.addDependency(() => regenerateGroupsAbortController.abort());

    const setGroupsLockedAbortController = new AbortController();
    window.addEventListener(
      "setGroupsLocked",
      (event) => {
        const detail = (event as any).detail as { competitionId: string; locked: boolean };
        this.actionSetGroupsLocked(detail.competitionId, detail.locked);
      },
      { signal: setGroupsLockedAbortController.signal },
    );
    this.addDependency(() => setGroupsLockedAbortController.abort());

    const resetPlayoffDivisionsAbortController = new AbortController();
    window.addEventListener(
      "resetPlayoffDivisions",
      (event) => {
        const detail = (event as any).detail as { competitionId: string };
        this.actionResetPlayoffDivisions(detail.competitionId);
      },
      { signal: resetPlayoffDivisionsAbortController.signal },
    );
    this.addDependency(() => resetPlayoffDivisionsAbortController.abort());

    const setPlayoffDivisionAbortController = new AbortController();
    window.addEventListener(
      "setPlayoffDivision",
      (event) => {
        const detail = (event as any).detail as {
          competitionId: string;
          entryId: string;
          division: number | undefined;
        };
        void this.actionSetPlayoffDivision(detail.competitionId, detail.entryId, detail.division);
      },
      { signal: setPlayoffDivisionAbortController.signal },
    );
    this.addDependency(() => setPlayoffDivisionAbortController.abort());

    this.addDependency(
      "queueCompetitionForGeneration",
      Model.events.on("tournament:matchesReset", (event: TournamentMatchesResetEvent) => {
        const { competition } = event;
        this.queueCompetitionForGeneration(competition.id!);
      }),
    );

    // Cleanup debounced generations on teardown
    this.addDependency(() => {
      for (const timeoutId of this.debouncedGenerations.values()) {
        clearTimeout(timeoutId);
      }
      this.debouncedGenerations.clear();
    });
  }

  override async changeProps(newProps: Props) {
    this.state.id = newProps.params.id;

    if (newProps.params.id !== this.state.tournamentSession?.id) {
      await this.loadTournamentSession(this.state.id);
    }
  }

  private handleAkahuOnboardingReturn() {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const status = params.get("akahu_status");
    if (!status) return;

    const errorDetail =
      params.get("akahu_error_description") || params.get("akahu_error") || "Bank account connection failed";

    const url = new URL(window.location.href);
    for (const key of [
      "akahu_status",
      "club_id",
      "akahu_error",
      "akahu_error_description",
      "error",
      "error_description",
      "connected",
    ]) {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);

    if (status === "success") {
      messagesService.addMessage(
        "Club bank account connected. Finish setting up your tournament payment method.",
        "success",
      );
    } else {
      messagesService.addMessage(errorDetail, "error");
    }

    routeService.openModal("/tournament/:id/signups", {
      // Payment is the second step in the signups wizard (0-based index 1).
      params: { id: this.state.id, initialStep: 1 },
    });
  }

  @withStateToggle("loading")
  async loadTournamentSession(id: string) {
    const sessionQuery = TournamentSession.query().eq("account_id", Account.current!.id);
    const tournamentSession = await sessionQuery.find(id);
    this.state.tournamentSession = tournamentSession;

    if (this.get(AppController).state.currentClub?.id !== tournamentSession.club_id) {
      this.get(AppController).actionSetCurrentClubById(this.state.tournamentSession.club_id!);
    }

    const competitionQuery = TournamentCompetition.query().eq("tournament_session_id", id);
    const playersQuery = TournamentPlayer.query().eq("tournament_session_id", id);
    const paymentMethodsQuery = TournamentPaymentMethod.query().eq("tournament_session_id", id);

    const competitionQueries = (competitions: TournamentCompetition[]) => {
      const competitionIds = competitions.map((c) => c.id);

      return {
        registrations: TournamentRegistration.query().in("tournament_competition_id", competitionIds),
        entries: TournamentEntry.query().in("tournament_competition_id", competitionIds),
        events: TournamentCompetitionEvent.query().in("tournament_competition_id", competitionIds),
        conflicts: TournamentCompetitionConflict.query().in("tournament_competition_id", competitionIds),
      };
    };

    const competitions = await competitionQuery.orderBy("created_at", "desc").all();
    this.state.competitions = new List(...competitions);

    const players = await playersQuery.orderBy("name", "desc").all();
    this.state.players = new List(...players);

    const paymentMethods = await paymentMethodsQuery.all();
    this.state.paymentMethods = new List(...paymentMethods);

    const queries = competitionQueries(competitions);
    const registrations = await queries.registrations.all();
    this.state.registrations = new List(...registrations);

    const entries = await queries.entries.orderBy("created_at", "desc").all();
    this.state.entries = new List(...entries);

    // Ensure entries are correct
    await this.actionEnsureEntries();

    this.state.matches = new List();

    // Run initial setup
    this.setupModelRelations();
    const debouncedSetupModelRelations = debounce(() => {
      this.setupModelRelations();
    }, 100);

    this.observe(() => {
      const queries = competitionQueries(this.state.competitions);

      queueMicrotask(async () => {
        this.resolveDependency("subscriptions");

        const subscriptions = await Promise.all([
          subscribeList(this.state.competitions, competitionQuery, {
            callback: debouncedSetupModelRelations,
          }),
          subscribeList(this.state.players, playersQuery, {
            callback: (update) => {
              debouncedSetupModelRelations();
              if (update.type === "UPDATE" && "checked_in_at" in (update.changes || {})) {
                for (const competition of this.state.competitions) {
                  if (competition.id) {
                    this.queueCompetitionForGeneration(competition.id);
                  }
                }
              }
            },
          }),
          subscribeList(this.state.registrations, queries.registrations, {
            callback: debouncedSetupModelRelations,
          }),
          subscribeList(this.state.entries, queries.entries, { callback: debouncedSetupModelRelations }),
          subscribeList(this.state.paymentMethods, paymentMethodsQuery, {
            callback: debouncedSetupModelRelations,
          }),
          sessionQuery.eq("id", id).subscribe({
            callback: (updates) => {
              const updateWithModel = updates.findLast((update) => update.model?.id === id);
              if (!updateWithModel) return;
              const { model } = updateWithModel;
              const previous = this.state.tournamentSession;
              if (previous?.id === model.id) {
                previous.copyCollectionsTo(model);
                previous.copyReferencesTo(model);
              }
              this.state.tournamentSession = model;
              debouncedSetupModelRelations();
            },
          }),
          competitionQuery.subscribe({
            callback: (updates) => {
              for (const update of updates) {
                // Only process updates that match the query (i.e., competitions for this tournament session)
                if (!competitionQuery.matches(update.model)) continue;

                const { model, type } = update;
                if (type === "INSERT" || type === "DELETE") {
                  this.debug("Queue competition for generation because it was inserted or deleted");
                  this.queueCompetitionForGeneration(model.id!);
                } else if (type === "UPDATE") {
                  if ("status" in update.changes && model.status !== update.changes.status) {
                    // Reset matches when starting registration
                    if (model.isRegistration) {
                      this.debug("Resetting matches for competition because it was started");
                      this.actionCleanupMatches(model.id!);
                    }

                    // Do something on completetion?
                    if (model.isCompleted) {
                      this.state.showCompetitionCompletedModal[model.id!] = true;
                    }
                  }
                }
              }
              debouncedSetupModelRelations();
            },
            source: this.generationSubscriptionSources,
          }),
          queries.entries.subscribe({
            callback: (updates) => {
              for (const update of updates) {
                const { model, type } = update;
                if (
                  type === "INSERT" ||
                  type === "DELETE" ||
                  (type === "UPDATE" &&
                    queries.entries.matches(model) &&
                    ("seed" in update.changes || "withdrawn" in update.changes))
                ) {
                  this.debug(
                    "Queue competition for generation because entry was inserted or deleted or seed or withdrawn was changed",
                  );
                  this.queueCompetitionForGeneration(model.tournament_competition_id!);
                }
              }
              debouncedSetupModelRelations();
            },
            source: this.generationSubscriptionSources,
          }),
          queries.events.subscribe({
            callback: (updates) => {
              for (const update of updates) {
                const matches = queries.events.matches(update.model);
                console.log(
                  `[TournamentConflict] event ${update.type} source=${update.source} id=${update.model.id} competition=${update.model.tournament_competition_id ?? "none"} type=${update.model.event_type ?? "unknown"} matches=${matches} generating=${this.state.generatingMatches}`,
                );
                if (!matches) {
                  // Broadcast payloads are only a row id. If the refetch has not
                  // landed yet, the stub cannot be matched to a competition.
                  if (!update.model.tournament_competition_id) {
                    void this.reconcileRemoteTournamentEvents();
                  }
                  continue;
                }
                this.queueCompetitionForGeneration(update.model.tournament_competition_id!);
              }
              debouncedSetupModelRelations();
            },
            source: this.generationSubscriptionSources,
          }),
          queries.conflicts.subscribe({
            callback: (updates) => {
              for (const update of updates) {
                if (!queries.conflicts.matches(update.model)) continue;
                this.queueCompetitionForGeneration(update.model.tournament_competition_id!);
              }
              debouncedSetupModelRelations();
            },
            source: this.generationSubscriptionSources,
          }),
        ]);
        this.addDependency("subscriptions", () =>
          subscriptions.forEach((unsubscribe) => {
            unsubscribe();
          }),
        );
      });
    });

    observe(
      () => {
        this.state.matchEntries = this.state.matches.reduce(
          (acc, match) => {
            acc[match.uniqueId] = this.state.entries
              .filter((e) => e.id === match.entries?.current[0] || e.id === match.entries?.current[1])
              .map((e) => e.uniqueId);
            return acc;
          },
          {} as Record<string, string[]>,
        );
      },
      { scheduler: debounceScheduler(100) },
    );

    this.debug("Queue all competitions for generation on load");
    this.queueAllCompetitionsForGeneration();
    this.watchRemoteTournamentEvents();
    this.handleAkahuOnboardingReturn();
  }

  /**
   * Account broadcasts are not replayed. While this page is open, re-read the
   * event log and regenerate when another device has appended events we have
   * not folded in yet.
   */
  private watchRemoteTournamentEvents() {
    this.resolveDependency("eventReconcile");
    const reconcile = () => {
      void this.reconcileRemoteTournamentEvents();
    };
    const timer = setInterval(reconcile, this.REMOTE_EVENT_RECONCILE_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") reconcile();
    };
    document.addEventListener("visibilitychange", onVisible);
    this.addDependency("eventReconcile", () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    });
  }

  private async remoteEventStreamToken(competitionId: string) {
    const events = await TournamentCompetitionEvent.query()
      .eq("tournament_competition_id", competitionId)
      .all();
    return tournamentEventStreamToken(events);
  }

  private noteLoadedEventToken(competitionId: string | undefined, adapter: TournamentDataAdapter) {
    if (!competitionId || adapter.lastLoadedEventToken === undefined) return;
    this.loadedEventTokens.set(competitionId, adapter.lastLoadedEventToken);
  }

  private async reconcileRemoteTournamentEvents() {
    if (this.reconcileInFlight) return;
    this.reconcileInFlight = true;
    try {
      for (const competition of [...this.state.competitions]) {
        const id = competition.id;
        if (!id) continue;
        const loaded = this.loadedEventTokens.get(id);
        if (loaded === undefined) continue;
        const remote = await this.remoteEventStreamToken(id);
        if (remote === loaded) continue;
        const eventCount = (token: string) => (token ? token.split(",").length : 0);
        console.log(
          `[TournamentConflict] reconcile remote events competition=${id} loadedEvents=${eventCount(loaded)} remoteEvents=${eventCount(remote)}`,
        );
        this.queueCompetitionForGenerationImmediate(id);
      }
    } catch (error) {
      console.error("[TournamentConflict] reconcile remote events failed", error);
    } finally {
      this.reconcileInFlight = false;
    }
  }

  getMatchEntries(match: TournamentMatch) {
    // Use match.entries?.current directly to preserve storage order
    const entryIds = match.entries?.current || [];
    return entryIds
      .filter((id): id is string => !!id)
      .map((id) => this.state.entries.findById(id))
      .filter(isPresent);
  }

  /** Slot ids like `r0:m0` are per-competition; always scope lookups by competition. */
  findProjectedMatch(competitionId: string | undefined, matchId: string | undefined) {
    if (!competitionId || !matchId) return undefined;
    return this.state.matches.find(
      (match) => match.id === matchId && match.tournament_competition_id === competitionId,
    );
  }

  private openConflictsForCompetition(competition?: TournamentCompetition): TournamentOpenConflict[] {
    if (!competition) return [];
    const conflicts = competition.format_data?.tournament_conflicts;
    if (!Array.isArray(conflicts)) return [];
    return conflicts.filter(
      (conflict): conflict is TournamentOpenConflict =>
        !!conflict && typeof conflict === "object" && (conflict as TournamentOpenConflict).status === "open",
    );
  }

  get openConflictsByCompetition(): Record<string, TournamentOpenConflict[]> {
    return this.state.competitions.reduce(
      (acc, competition) => {
        if (!competition.id) return acc;
        acc[competition.id] = this.openConflictsForCompetition(competition);
        return acc;
      },
      {} as Record<string, TournamentOpenConflict[]>,
    );
  }

  get openConflictedSlotIdsByCompetition(): Record<string, Set<string>> {
    return this.state.competitions.reduce(
      (acc, competition) => {
        if (!competition.id) return acc;
        const slotIds = new Set<string>();
        for (const conflict of this.openConflictsForCompetition(competition)) {
          const slotId = typeof conflict.payload?.slot_id === "string" ? conflict.payload.slot_id : undefined;
          if (slotId) slotIds.add(slotId);
        }
        acc[competition.id] = slotIds;
        return acc;
      },
      {} as Record<string, Set<string>>,
    );
  }

  hasOpenConflicts(competitionId?: string): boolean {
    if (!competitionId) return false;
    return (this.openConflictsByCompetition[competitionId]?.length || 0) > 0;
  }

  conflictsForSlot(competitionId?: string, slotId?: string): TournamentOpenConflict[] {
    if (!competitionId || !slotId) return [];
    const bareSlotId = getMatchSlotId({ id: slotId });
    return (this.openConflictsByCompetition[competitionId] || []).filter(
      (conflict) => conflict.payload?.slot_id === bareSlotId,
    );
  }

  get openConflictCount(): number {
    return Object.values(this.openConflictsByCompetition).reduce(
      (sum, conflicts) => sum + conflicts.length,
      0,
    );
  }

  competitionsRequiringGeneration: Set<string> = new Set();

  // Rate limiting to prevent feedback loops
  private generationHistory: Map<string, number[]> = new Map();
  private readonly MAX_GENERATIONS_PER_WINDOW = 5;
  private readonly GENERATION_WINDOW_MS = 10000; // 10 seconds
  // @ts-expect-error
  private readonly DEBOUNCE_DELAY_MS = window.__TEST_MODE ? 100 : 3000; // 3 seconds
  // Safety net for a very unlikely edge case. It only matters when two admins
  // set the winner for the same match at nearly the same time, pick different
  // winners, and the one-shot account broadcast for the other device's event
  // is missed. Remove or replace this poll if a better delivery mechanism
  // arrives (for example a replayable event stream). 30s in production.
  // Playwright and Tauri tests set window.__TEST_MODE (same flag as
  // DEBOUNCE_DELAY_MS) so the conflict spec does not wait on that interval.
  // @ts-expect-error
  private readonly REMOTE_EVENT_RECONCILE_MS = window.__TEST_MODE ? 1000 : 30_000;
  private loadedEventTokens = new Map<string, string>();
  private reconcileInFlight = false;
  // Track debounced generation timeouts
  private debouncedGenerations: Map<string, ReturnType<typeof setTimeout>> = new Map();

  queueAllCompetitionsForGeneration() {
    return this.queueCompetitionForGeneration(
      ...this.state.competitions.map((competition) => competition.id!),
    );
  }

  queueCompetitionForGenerationImmediate(...competitionId: string[]) {
    return this.queueCompetitionForGenerationInternal(competitionId, { bypassRateLimit: true });
  }

  queueCompetitionForGeneration(...competitionId: string[]) {
    return this.queueCompetitionForGenerationInternal(competitionId, { bypassRateLimit: false });
  }

  private queueCompetitionForGenerationInternal(
    competitionIds: string[],
    options: { bypassRateLimit: boolean },
  ) {
    const { bypassRateLimit } = options;
    competitionIds.forEach((id) => {
      // Check rate limit before queuing
      if (!bypassRateLimit && this.isGenerationRateLimited(id)) {
        const count = this.getRecentGenerationCount(id);
        console.log(
          `[TournamentConflict] generation rate-limited competition=${id} recent=${count} debounceMs=${this.DEBOUNCE_DELAY_MS} generating=${this.state.generatingMatches}`,
        );
        this.debug(
          `Rate limit hit for competition ${id}. ${count} generations in last ${this.GENERATION_WINDOW_MS}ms. Debouncing for ${this.DEBOUNCE_DELAY_MS}ms`,
        );

        // Cancel existing debounced generation if any
        const existingTimeout = this.debouncedGenerations.get(id);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Schedule debounced generation
        const timeoutId = setTimeout(() => {
          this.debouncedGenerations.delete(id);
          this.competitionsRequiringGeneration.add(id);
          this.generateMatches();
        }, this.DEBOUNCE_DELAY_MS);

        this.debouncedGenerations.set(id, timeoutId);
        return;
      }
      this.competitionsRequiringGeneration.add(id);
    });
    setTimeout(() => {
      return this.generateMatches();
    }, 100);
  }

  private isGenerationRateLimited(competitionId: string): boolean {
    const now = Date.now();
    const history = this.generationHistory.get(competitionId) || [];

    // Remove timestamps outside the window
    const recentHistory = history.filter((timestamp) => now - timestamp < this.GENERATION_WINDOW_MS);

    // Check if we've exceeded the limit
    if (recentHistory.length >= this.MAX_GENERATIONS_PER_WINDOW) {
      return true;
    }

    return false;
  }

  private getRecentGenerationCount(competitionId: string): number {
    const now = Date.now();
    const history = this.generationHistory.get(competitionId) || [];
    return history.filter((timestamp) => now - timestamp < this.GENERATION_WINDOW_MS).length;
  }

  private recordGeneration(competitionId: string) {
    const now = Date.now();
    const history = this.generationHistory.get(competitionId) || [];

    // Remove old timestamps and add new one
    const recentHistory = history.filter((timestamp) => now - timestamp < this.GENERATION_WINDOW_MS);
    recentHistory.push(now);
    this.generationHistory.set(competitionId, recentHistory);
  }

  @withStateToggle("generatingMatches", { returnIfTrue: true, queueIfRunning: true })
  @withBatch
  async generateMatches() {
    const competitionIds = [...this.competitionsRequiringGeneration.values()];
    console.log(
      `[TournamentConflict] generateMatches start ids=${competitionIds.join(",") || "(none)"} queuedAfterClear will-run=${competitionIds.length}`,
    );
    const competitions = competitionIds
      .map((id) => this.state.competitions.findById(id))
      .filter((competition): competition is NonNullable<typeof competition> => !!competition);
    this.competitionsRequiringGeneration.clear();

    await SubscriptionManager.instance.startBatch(async () => {
      const projectedByCompetition = new Map<string, TournamentMatch[]>();
      let anyProjectionChanged = false;

      for (const competition of competitions) {
        this.recordGeneration(competition.id!);
        this.debug("Generating matches for competition", competition.id);
        console.log("Generating matches for competition", competition.id);

        const result = await this.generateCompetitionMatches(competition);
        if (!result) continue;
        projectedByCompetition.set(competition.id!, result.projectedMatches);
        anyProjectionChanged = anyProjectionChanged || result.projectionChanged;
      }

      if (anyProjectionChanged) {
        const regeneratingIds = new Set(projectedByCompetition.keys());
        const unaffectedMatches = this.state.matches.filter(
          (match) =>
            !match.tournament_competition_id || !regeneratingIds.has(match.tournament_competition_id),
        );
        const projectedMatches = [...projectedByCompetition.values()].flat();
        this.state.matches = new List(...unaffectedMatches, ...projectedMatches);
      }
    });

    if (this.competitionsRequiringGeneration.size > 0) {
      this.debug("Some competitions still require generation");
      console.log(
        `[TournamentConflict] generateMatches still queued after run count=${this.competitionsRequiringGeneration.size}`,
      );
      setTimeout(() => {
        this.generateMatches();
      }, 1000);
    } else {
      console.log("[TournamentConflict] generateMatches finished with empty queue");
    }
  }

  private async generateCompetitionMatches(
    competition: TournamentCompetition,
  ): Promise<{ projectedMatches: TournamentMatch[]; projectionChanged: boolean } | null> {
    function replaceUuidsWithEntries(text: string) {
      // Extract the Entries line
      const entriesMatch = text.match(/Entries:\s*(.+)/);
      if (!entriesMatch) {
        return "";
      }

      // Extract all UUIDs from the Entries line
      const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
      const entriesLine = entriesMatch[1];
      const uuids = [...new Set(entriesLine.match(uuidRegex) || [])];

      // Create mapping from UUID to e1, e2, e3, etc.
      const uuidMap = new Map();
      uuids.forEach((uuid, index) => {
        uuidMap.set(uuid, `e${index + 1}`);
      });

      // Replace all UUIDs in the text
      let result = text;
      uuidMap.forEach((replacement, uuid) => {
        result = result.replace(new RegExp(uuid, "g"), replacement);
      });

      return result;
    }

    try {
      const adapter = new TournamentDataAdapter();
      const converter = new TournamentStateConverter();
      const previousCompetitionMatches = this.state.matches.filter(
        (match) => match.tournament_competition_id === competition.id,
      );

      let state = await adapter.loadState(competition);
      this.debug(converter.stateToAscii(state));
      console.log(replaceUuidsWithEntries(converter.stateToAscii(state)));

      let projectedMatches = await adapter.loadProjectedMatches(competition);

      // Automatic no-show voids once every other group fixture is completed.
      if (competition.format === "RoundRobin" && competition.isActive) {
        const uncheckedEntryIds = new Set<string>();
        for (const entry of this.state.entries) {
          if (entry.tournament_competition_id !== competition.id || !entry.id) continue;
          try {
            if (!entry.isCheckedIn) uncheckedEntryIds.add(entry.id);
          } catch {
            // Relations not loaded — skip.
          }
        }
        const playoffsGenerated = projectedMatches.some((match) => match.format_data?.phase === "playoff");
        const voidCommands = planAutomaticNoShowVoids({
          matches: projectedMatches,
          uncheckedEntryIds,
          playoffsGenerated,
        });
        if (voidCommands.length > 0) {
          for (const command of voidCommands) {
            await adapter.appendEvent({
              competition,
              eventType: "match_voided",
              payload: {
                slot: {
                  slot_id: command.slot_id,
                  round: command.round,
                  match_index: command.match_index,
                  ...(command.division !== undefined ? { division: command.division } : {}),
                },
                reason: command.reason,
                responsible_entry_ids: command.responsible_entry_ids,
              },
            });
          }
          state = await adapter.loadState(competition);
          projectedMatches = await adapter.loadProjectedMatches(competition);
        }
      }

      const previousFingerprint = this.projectionFingerprint(previousCompetitionMatches);
      const nextFingerprint = this.projectionFingerprint(projectedMatches);
      const projectionChanged = previousFingerprint !== nextFingerprint;

      await this.persistRoundRobinGroups(competition, state);
      const persistNeedsRegenerate = await this.persistRoundRobinDivisions(competition, state);
      if (persistNeedsRegenerate && competition.id) {
        this.noteLoadedEventToken(competition.id, adapter);
        this.competitionsRequiringGeneration.add(competition.id);
        return { projectedMatches: previousCompetitionMatches, projectionChanged: false };
      }

      if (state.metadata && Object.keys(state.metadata).length > 0) {
        this.debug("Tournament metadata:", state.metadata);
        if (Array.isArray((state.metadata as Record<string, unknown>).conflicts)) {
          competition.format_data = {
            ...(competition.format_data || {}),
            tournament_conflicts: (state.metadata as Record<string, unknown>).conflicts,
          };
          await competition.save();
        }
      }

      this.noteLoadedEventToken(competition.id, adapter);
      return { projectedMatches, projectionChanged };
    } catch (error) {
      console.error("Error generating matches:", error);
      messagesService.addMessage(`${error}`, "error");
      captureException(error, {
        tags: {
          competitionId: competition.id,
          competitionName: competition.name,
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
      return null;
    }
  }

  @withBatch
  async actionCleanupAllMatches() {
    const competitionIds = this.state.competitions
      .map((competition) => competition.id)
      .filter((id): id is string => !!id);
    if (competitionIds.length === 0) return;
    // Queue every competition in one pass (bypass rate limit) then generate once so
    // multi-competition sessions do not drop later competitions via returnIfTrue races.
    this.queueCompetitionForGenerationImmediate(...competitionIds);
    await this.generateMatches();
  }

  @withBatch
  async actionSetGroupsLocked(competitionId: string, locked: boolean) {
    const competition = this.state.competitions.findById(competitionId);
    if (!competition) {
      messagesService.addMessage("Competition not found", "error");
      return;
    }

    if (competition.format !== "RoundRobin") {
      messagesService.addMessage("Group locking is only available for round robin format", "error");
      return;
    }

    if (locked && !roundRobinHasGroups(competition)) {
      messagesService.addMessage("Set up groups before locking them", "error");
      return;
    }

    try {
      await setRoundRobinGroupsLocked(competition, locked);
      messagesService.addMessage(
        locked
          ? "Groups are locked. They will stay as they are unless you move someone by hand."
          : "Groups are unlocked. New entries can be placed automatically again.",
        "success",
      );
    } catch (error) {
      console.error("Error updating group lock:", error);
      messagesService.addMessage(
        `Error updating group lock: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
      captureException(error, {
        tags: {
          competitionId,
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
    }
  }

  @withBatch
  async actionRegenerateGroups(competitionId: string) {
    const competition = this.state.competitions.findById(competitionId);
    if (!competition) {
      messagesService.addMessage("Competition not found", "error");
      return;
    }

    // Only allow regeneration for round robin format
    if (competition.format !== "RoundRobin") {
      messagesService.addMessage("Group regeneration is only available for round robin format", "error");
      return;
    }

    if (roundRobinGroupsLocked(competition)) {
      messagesService.addMessage("Unlock groups before regenerating them", "error");
      return;
    }

    try {
      // Clear groups metadata from competition format_data
      if (!competition.format_data) {
        competition.format_data = {};
      }
      delete competition.format_data.groups;
      competition.changed("format_data");
      await competition.save();

      // Clear groups from all entry format_data for this competition
      const entries = this.state.entries.filter((e) => e.tournament_competition_id === competitionId);
      for (const entry of entries) {
        if (entry.format_data?.group) {
          console.log("[GroupWrite][TournamentController.actionRegenerateGroups.clear]", {
            entryId: entry.id,
            competitionId,
            previousGroup: entry.format_data.group,
            nextGroup: undefined,
          });
          delete entry.format_data.group;
          entry.changed("format_data");
          await entry.save();
        }
      }

      // Call actionCleanupMatches to regenerate groups and matches
      await this.actionCleanupMatches(competitionId);

      messagesService.addMessage("Groups have been regenerated successfully", "success");
    } catch (error) {
      console.error("Error regenerating groups:", error);
      messagesService.addMessage(
        `Error regenerating groups: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
      captureException(error, {
        tags: {
          competitionId,
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
    }
  }

  @withBatch
  async actionResetPlayoffDivisions(competitionId: string) {
    const competition = this.state.competitions.findById(competitionId);
    if (!competition) {
      messagesService.addMessage("Competition not found", "error");
      return;
    }

    if (competition.format !== "RoundRobin") {
      messagesService.addMessage("Playoff division reset is only available for round robin format", "error");
      return;
    }

    const playoffMatches = this.state.matches.filter(
      (match) => match.tournament_competition_id === competitionId && match.format_data?.phase === "playoff",
    );
    if (playoffMatches.some((match) => match.isInProgress || match.isComplete || match.isVoided)) {
      messagesService.addMessage(
        "Playoff divisions cannot be reset after playoff matches have started",
        "error",
      );
      return;
    }

    try {
      const adapter = new TournamentDataAdapter();
      await adapter.appendEvent({
        competition,
        eventType: "playoff_divisions_reset",
        payload: {},
      });
      await this.actionGenerateCompetitionNow(competitionId);
      messagesService.addMessage("Playoff divisions have been reset to the splitting rules", "success");
    } catch (error) {
      console.error("Error resetting playoff divisions:", error);
      messagesService.addMessage(
        `Error resetting playoff divisions: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error",
      );
      captureException(error, {
        tags: {
          competitionId,
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
    }
  }

  @withStateToggle("cleaningUpMatches", { returnIfTrue: true })
  @withBatch
  async actionCleanupMatches(competitionId: string) {
    const competition = this.state.competitions.findById(competitionId);
    console.log("Cleaning up matches for competition", competitionId);
    console.log("Competition", competition);
    if (!competition) return;

    await SubscriptionManager.startBatch(async () => {
      const matches = this.state.matches.filter((m) => m.tournament_competition_id === competitionId);

      const _matchesToDelete = [];
      for (const match of matches) {
        if (!match.hasEntries) {
          // matchesToDelete.push(match);
        } else if (competition.format === "RoundRobin" && (match.isPending || match.isReady)) {
          // matchesToDelete.push(match);
        }
      }
      // await TournamentMatch.destroyBatch(matchesToDelete);

      // if (competition.format === "RoundRobin") {
      //   competition.format_data = {};
      //   await competition.save();

      //   const entries = this.state.entries.filter((e) => e.tournament_competition_id === competitionId);
      //   for (const entry of entries) {
      //     entry.format_data = {};
      //     await entry.save();
      //   }
      // }
    });

    console.log("Queueing competition for generation", competitionId);
    this.queueCompetitionForGenerationImmediate(competitionId);
    console.log("Generating matches");
    await this.generateMatches();
  }

  actionOpenEditMatchModal(match: TournamentMatch, options: { matchList?: string[] } = {}) {
    if (!match.tournament_competition_id || !match.id) return;

    // Open the match edit modal with the competition ID
    routeService.openModal("/tournament/:id/matches/:matchId/edit", {
      params: { id: match.tournament_competition_id, matchId: match.id },
      state: {
        attributes: {
          tournamentId: match.tournament_competition_id,
          tournament_competition_id: match.tournament_competition_id,
        },
        matchList: options.matchList,
      },
    });
  }

  async actionEnableSignUp(value: boolean) {
    if (!this.state.tournamentSession.id) return;

    this.state.tournamentSession.is_public = value;
    await this.state.tournamentSession.save();
  }

  actionOpenEditEntryModal(entry: TournamentEntry) {
    return routeService.openModal("/tournament/:id/entries/:entryId/edit", {
      params: {
        id: this.state.tournamentSession.id!,
        entryId: entry.id!,
      },
    });
  }

  actionOpenEditPlayerModal(player: TournamentPlayer) {
    return routeService.openModal("/tournament/:id/players/:playerId/edit", {
      params: {
        id: this.state.tournamentSession.id!,
        playerId: player.id!,
      },
    });
  }

  actionCloseCompetitionWinnerModal(competitionId: string) {
    this.state.showCompetitionCompletedModal[competitionId] = false;
  }

  async actionStartTournament() {
    this.setupModelRelations();
    await this.state.tournamentSession.startTournaments();
  }

  /**
   * Check if all competitions have winners
   */
  get canCompleteTournament() {
    if (this.state.tournamentSession.isCompleted) return false;
    if (!this.state.tournamentSession.isActive) return false;

    // Check if all competitions have winners
    for (const competition of this.state.competitions) {
      if (!competition.format_data) return false;
      if (!competition.format_data.winners) return false;
      if (!Array.isArray(competition.format_data.winners)) return false;
      if (competition.format_data.winners.length === 0) return false;
    }

    return true;
  }

  /**
   * Get list of competitions without winners (open competitions)
   */
  get competitionsWithoutWinners() {
    return this.state.competitions.filter((competition) => {
      if (!competition.format_data) return true;
      if (!competition.format_data.winners) return true;
      if (!Array.isArray(competition.format_data.winners)) return true;
      return competition.format_data.winners.length === 0;
    });
  }

  async actionSelectWinners(competitionId: string, winners: Array<[string, Record<string, any>]>) {
    const competition = this.state.competitions.findById(competitionId);
    if (!competition) return;

    const formatData = competition.format_data || {};
    formatData.winners = winners;
    competition.format_data = formatData;
    await competition.save();

    // Trigger match generation to sync state and check completion
    this.queueCompetitionForGeneration(competitionId);
    await this.generateMatches();
  }

  /**
   * Get list of competitions with winners (completed competitions)
   */
  get competitionsWithWinners() {
    return this.state.competitions.filter((competition) => {
      if (!competition.format_data) return false;
      if (!competition.format_data.winners) return false;
      if (!Array.isArray(competition.format_data.winners)) return false;
      return competition.format_data.winners.length > 0;
    });
  }

  actionShowCompleteTournamentModal() {
    this.state.showCompleteTournamentModal = true;
  }

  actionHideCompleteTournamentModal() {
    this.state.showCompleteTournamentModal = false;
  }

  @withStateToggle("completingTournament")
  async actionCompleteTournament() {
    try {
      // Ensure competitions are loaded
      if (!this.state.tournamentSession.competitions.loaded) {
        await this.state.tournamentSession.competitions.load();
      }

      await this.state.tournamentSession.complete();
      this.state.showCompleteTournamentModal = false;
      messagesService.addMessage("Tournament completed successfully", "success");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      messagesService.addMessage(`Failed to complete tournament: ${errorMessage}`, "error");
      captureException(error, {
        tags: {
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
    }
  }

  actionShowThankYouModal() {
    this.state.thankYouText = this.state.tournamentSession.thank_you_text ?? "";
    this.state.showThankYouModal = true;
  }

  actionHideThankYouModal() {
    this.state.showThankYouModal = false;
  }

  actionSetThankYouText(value: string) {
    this.state.thankYouText = value;
  }

  @withStateToggle("sendingThankYou")
  async actionSendThankYou() {
    const session = this.state.tournamentSession;
    if (!session.id) return;

    try {
      const { recipientCount } = await actions.notifications.sendTournamentThankYou({
        tournamentSessionId: session.id,
        thankYouText: this.state.thankYouText || null,
      });

      session.thank_you_text = this.state.thankYouText || undefined;
      session.thank_you_sent_at = new Date();
      this.state.showThankYouModal = false;

      const recipientLabel = recipientCount === 1 ? "1 player" : `${recipientCount} players`;
      messagesService.addMessage(`Thank-you email sent to ${recipientLabel}`, "success");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      messagesService.addMessage(`Failed to send thank-you email: ${errorMessage}`, "error");
      captureException(error, {
        tags: {
          tournamentSessionId: session.id,
        },
      });
    }
  }

  setupModelRelations() {
    setupModelRelations({
      tournamentSession: this.state.tournamentSession,
      competitions: this.state.competitions,
      players: this.state.players,
      registrations: this.state.registrations,
      entries: this.state.entries,
      matches: this.state.matches,
      paymentMethods: this.state.paymentMethods,
    });
  }

  /**
   * Get standings data for a competition
   * @param competitionId ID of the competition
   * @returns Standings data for the competition
   */
  async getCompetitionStandings(competitionId: string) {
    try {
      const competition = this.state.competitions.findById(competitionId);
      if (!competition) {
        throw new Error("Competition not found");
      }

      const adapter = new TournamentDataAdapter();
      const state = await adapter.loadState(competition);

      const format = getFormat(competition.format as FormatName);
      return format.getStandings(state, competition.format_settings);
    } catch (error) {
      console.error("Error getting competition standings:", error);
      messagesService.addMessage(`${error}`, "error");
      captureException(error, {
        tags: {
          competitionId,
          tournamentSessionId: this.state.tournamentSession.id,
        },
      });
      return null;
    }
  }

  @withBatch
  async actionEnsureEntries() {
    const session = this.state.tournamentSession;
    const entries = this.state.entries;
    const registrations = this.state.registrations;
    const players = this.state.players;

    await SubscriptionManager.startBatch(async () => {
      // Delete entries with no registrations:
      const entriesToDelete: TournamentEntry[] = [];
      for (const entry of entries) {
        if (!registrations.some((r) => r.tournament_entry_id === entry.id)) {
          entriesToDelete.push(entry);
        }
      }
      await TournamentEntry.destroyBatch(entriesToDelete);

      // Delete registrations with no player:
      const registrationsToDelete: TournamentRegistration[] = [];
      for (const registration of registrations) {
        if (!players.some((p) => p.id === registration.tournament_player_id)) {
          registrationsToDelete.push(registration);
        }
      }
      await TournamentRegistration.destroyBatch(registrationsToDelete);

      registrationsToDelete.forEach((r) => {
        registrations.remove(r);
      });
      entriesToDelete.forEach((e) => {
        entries.remove(e);
      });

      // Create entries for registrations with no entry:
      const newEntries: TournamentEntry[] = [];
      await Promise.all(
        registrations.map(async (registration) => {
          if (!entries.some((e) => !e.is_deleted && e.id === registration.tournament_entry_id)) {
            const player = players.find((p) => p.id === registration.tournament_player_id);
            if (!player) return;

            const entry = new TournamentEntry({
              account_id: session.account_id,
              club_id: session.club_id,
              tournament_competition_id: registration.tournament_competition_id,
              name: player.name,
            });
            await entry.save();
            newEntries.push(entry);

            registration.tournament_entry_id = entry.id;
            await registration.save();
          }
        }),
      );
      // Add new entries to the list so they're available for setupModelRelations
      newEntries.forEach((entry) => {
        entries.insert(entry);
      });
    });
  }

  async actionDropEntry(dragData: TournamentEntryDragData, dropData: TournamentEntryDragData) {
    const adapter = new TournamentDataAdapter();
    const setEntries = async (
      match: TournamentMatch,
      nextEntries: [string | undefined, string | undefined],
    ) => {
      if (!match.tournament_competition_id) return;
      const competition = this.state.competitions.findById(match.tournament_competition_id);
      if (!competition) return;

      await adapter.appendEvent({
        competition,
        eventType: "match_entries_set",
        payload: {
          slot: {
            slot_id: getMatchSlotId(match),
            round: match.round || 0,
            match_index: match.match_index || 0,
            division:
              typeof match.format_data?.division === "number"
                ? match.format_data.division
                : Number(match.format_data?.division),
          },
          entries: nextEntries,
          locked: true,
        },
      });
      this.queueCompetitionForGenerationImmediate(match.tournament_competition_id);
    };

    if (
      isPresent(dragData.matchId) &&
      dragData.matchId === dropData.matchId &&
      dragData.entry?.id !== dropData.entry?.id
    ) {
      const match = this.state.matches.findById(dragData.matchId);
      if (!match) return;

      if (dropData.entryIndex === 0) {
        await setEntries(match, [dragData.entry?.id, dropData.entry?.id]);
      } else {
        await setEntries(match, [dropData.entry?.id, dragData.entry?.id]);
      }
      return;
    }

    if (isPresent(dragData.matchId) && isPresent(dropData.matchId)) {
      const sourceMatch = this.state.matches.findById(dragData.matchId);

      if (sourceMatch && dragData.entry?.id && (sourceMatch.isPending || sourceMatch.isReady)) {
        const sourceEntries = [...(sourceMatch.entries?.current || [])] as [
          string | undefined,
          string | undefined,
        ];
        sourceEntries[dragData.entryIndex] = undefined;

        // Swap with the target entry if it exists
        if (dropData.entry?.id) {
          sourceEntries[dragData.entryIndex] = dropData.entry.id;
        }
        await setEntries(sourceMatch, sourceEntries);
      }
    }

    if (isPresent(dropData.matchId)) {
      const targetMatch = this.state.matches.findById(dropData.matchId);
      if (!targetMatch) return;
      if (targetMatch.isComplete) return;

      if (dragData.entry?.id && (targetMatch.isPending || targetMatch.isReady)) {
        const targetEntries = [...(targetMatch.entries?.current || [])] as [
          string | undefined,
          string | undefined,
        ];
        targetEntries[dropData.entryIndex] = dragData.entry.id;
        await setEntries(targetMatch, targetEntries);
      }
    }
  }

  /**
   * Register tournament-related resources for AI agent access
   */
  private registerAgentResources() {
    // Build format schema context once
    const formatSchemas = Object.entries(formats)
      .map(([name, format]) => {
        const schema = format.getSettingsSchema();
        const schemaDescription = Object.entries(schema)
          .map(([key, field]) => {
            const typedField = field as SchemaField;
            const desc = typedField.description || key;
            const type = typedField.type;
            const defaultVal = typedField.default !== undefined ? ` (default: ${typedField.default})` : "";
            const min =
              typedField.type === "number" && "minimum" in typedField && typedField.minimum !== undefined
                ? ` (min: ${typedField.minimum})`
                : "";
            const max =
              typedField.type === "number" && "maximum" in typedField && typedField.maximum !== undefined
                ? ` (max: ${typedField.maximum})`
                : "";
            return `  - ${key}: ${type} - ${desc}${defaultVal}${min}${max}`;
          })
          .join("\n");
        return `${name}:\n${schemaDescription}`;
      })
      .join("\n\n");

    // Register tournament sessions resource
    this.addDependency(
      agentToolsService.addResources(this, {
        context:
          () => `The user is viewing and managing a tournament session (tournament_sessions resource) with related competitions, players, registrations, and entries.
        Tournament flow: setup (players register, pay, admin confirms) → registration (admin checks in players) → active (games being played) → completed (all competitions finished).
        Available pages:
        - overview: set up competitions and configure public registration portal
        - simulation: shown as scenarios, simulate tournament duration with different settings, courts, and entries. For any duration questions come here. Always refer as scenarios
        - players: manage tournament players
        - entries: manage tournament entries (accessed via players tab sub-navigation)
        - registration: check in arriving players on tournament day
        - matches: view competition structure (bracket/round-robin/swiss layout depending on format), entry flow, and manually reassign entries if needed (auto-manage preferred)
        - games: manage active matches - assign to courts, start timers, record results and winners
        - stats: view tournament statistics and winners (most useful after completion)
        
        IMPORTANT - Public Registration Portal Configuration:
        The "Sign ups" panel on the tournament overview page is for configuring the entire public registration portal experience that players will see. This includes:
        - Landing page: description and banner image that players see when they visit the registration link
        - Payment configuration: payment methods (Stripe, bank transfer, etc.) and whether payment is "by player" or "by entry"
        - Confirmation message: custom message players receive after the admin confirms their entry
        - Registration link: the public URL that players can use to sign up (displayed in the Sign ups panel once enabled)
        
        To configure any of these settings:
        1. On the tournament overview page, look for the "Sign ups" panel
        2. Click the "Configure" button in the Sign ups panel
        3. This opens a wizard with steps for: Description (banner image and description), Payment (payment methods and payment type), Confirmation (confirmation message), and Enabled (turn on signups and view registration link)
        4. These settings are NOT in the initial tournament creation wizard - they are in the separate Configure wizard accessible from the Sign ups panel

        Creating competitions (wizard):
        1. Call open_new_tournament_competition_modal to open the new competition modal.
        2. Use get_form_state at each wizard step.
        3. Select entry type (singles or doubles).
        4. Select format by UI label ("Single Elimination", "Round Robin") or stored value (SingleElimination, RoundRobin).
        5. Click through any format settings steps.
        6. Fill the competition name on the details step.
        7. Click the exact "Create competition" button from get_form_state form.buttons.
        8. Repeat from step 1 for each additional competition.
        `,
        definitions: {
          tournament_sessions: resource("tournament_sessions", TournamentSession, {
            list: () => (this.state.tournamentSession ? new List(this.state.tournamentSession) : new List()),
          }),
          tournament_competitions: resource("tournament_competitions", TournamentCompetition, {
            list: () => this.state.competitions,
            context: () => `Tournament Format Schemas:
        Each format has specific settings that can be configured in format_settings. Here are the available formats and their settings:

        ${formatSchemas}

        Special Notes:
        - For RoundRobin format with divisions: divisionSplittingRules is a JSON string with format: {"rules": [{"positions": [1], "division": 1}, {"positions": [2,3], "division": 2}]}
        - Positions in divisionSplittingRules are 1-indexed (1 = first place, 2 = second place, etc.)
        - When enablePlayoffs is true and divisions > 1, you must set qualifiersPerGroup to match the number of positions covered by divisionSplittingRules
        - Example: "top player from each group to division 1, second player to division 2, third and fourth to division 3" translates to:
          qualifiersPerGroup: 4, divisions: 3, divisionSplittingRules: '{"rules":[{"positions":[1],"division":1},{"positions":[2],"division":2},{"positions":[3,4],"division":3}]}'
        `,
            fields: {
              name: { required: true },
              format: {},
              format_settings: {},
              entry_type: {},
              min_entries: {},
              max_entries: {},
            },
            update: async (id, data) => {
              const competition = this.state.competitions.findById(id);
              if (!competition) throw new Error(`competition with id ${id} not found`);

              // Handle format change - need to validate format_settings against new format schema
              if (data.format && data.format !== competition.format) {
                const newFormat = getFormat(data.format as FormatName);
                const schema = newFormat.getSettingsSchema();

                // If format_settings provided, validate them
                if (data.format_settings) {
                  // Validate that all keys in format_settings exist in schema
                  const invalidKeys = Object.keys(data.format_settings).filter((key) => !(key in schema));
                  if (invalidKeys.length > 0) {
                    throw new Error(
                      `Invalid format_settings keys for ${data.format} format: ${invalidKeys.join(", ")}. Valid keys: ${Object.keys(schema).join(", ")}`,
                    );
                  }
                } else {
                  // If no format_settings provided, use defaults from schema
                  const defaultSettings: Record<string, any> = {};
                  Object.entries(schema).forEach(([key, field]) => {
                    const typedField = field as SchemaField;
                    if (typedField.default !== undefined) {
                      defaultSettings[key] = typedField.default;
                    }
                  });
                  data.format_settings = defaultSettings;
                }
              } else if (data.format_settings && competition.format) {
                // Validate format_settings against current format schema
                const currentFormat = getFormat(competition.format as FormatName);
                const schema = currentFormat.getSettingsSchema();
                const invalidKeys = Object.keys(data.format_settings).filter((key) => !(key in schema));
                if (invalidKeys.length > 0) {
                  throw new Error(
                    `Invalid format_settings keys for ${competition.format} format: ${invalidKeys.join(", ")}. Valid keys: ${Object.keys(schema).join(", ")}`,
                  );
                }
              }

              // Update competition
              if (data.format) {
                competition.format = data.format;
              }
              if (data.format_settings !== undefined) {
                competition.format_settings = data.format_settings;
              }
              if (data.name !== undefined) {
                competition.name = data.name;
              }
              if (data.entry_type !== undefined) {
                competition.entry_type = data.entry_type;
              }
              if (data.min_entries !== undefined) {
                competition.min_entries = data.min_entries;
              }
              if (data.max_entries !== undefined) {
                competition.max_entries = data.max_entries;
              }

              await competition.save();

              // Queue match regeneration if format or format_settings changed
              if (data.format || data.format_settings) {
                this.queueCompetitionForGeneration(id);
              }

              return competition;
            },
          }),
          tournament_players: resource("tournament_players", TournamentPlayer, {
            list: () => this.state.players,
            fields: {
              name: { required: true },
              email: {},
              mobile: {},
            },
            add: async (data) => {
              const player = new TournamentPlayer({
                ...data,
                account_id: Account.current!.id,
                tournament_session_id: this.state.tournamentSession.id,
              });
              await player.save();
              return player;
            },
            update: async (id, data) => {
              const player = this.state.players.findById(id);
              if (!player) throw new Error(`player with id ${id} not found`);
              player.writeAttributes(data);
              await player.save();
              return player;
            },
          }),
          tournament_registrations: resource("tournament_registrations", TournamentRegistration, {
            list: () => this.state.registrations,
            fields: {
              tournament_competition_id: { required: true },
              tournament_entry_id: {},
              tournament_player_id: { required: true },
            },
            add: async (data) => {
              const registration = new TournamentRegistration(data);
              registration.account_id = Account.current!.id;
              registration.club_id = this.state.tournamentSession.club_id;
              await registration.save();
              // Load player and competition so computed properties like name and status work
              await Promise.all([registration.player.load(), registration.competition.load()]);
              return registration;
            },
            update: async (id, data) => {
              if (data.tournament_player_id) {
                throw new Error(
                  "Cannot update tournament_player_id on a registration. You can update tournament_entry_id to move the player to a different entry, or create a new registration to add them to a different competition",
                );
              }

              const registration = this.state.registrations.findById(id);
              if (!registration) throw new Error(`registration with id ${id} not found`);
              registration.writeAttributes(data);
              console.log("Updating registration", registration.id, data);
              await registration.save();
              console.log("Registration saved", registration.id);
              // Reload player and competition so computed properties like name and status work
              await Promise.all([registration.player.load(), registration.competition.load()]);
              return registration;
            },
          }),
          tournament_entries: resource("tournament_entries", TournamentEntry, {
            list: () => this.state.entries,
            fields: {
              seed: {},
              tournament_competition_id: { required: true },
              withdrawn: {},
              group: {},
              division: {},
            },
            add: async (data) => {
              const { group, division, ...otherData } = data;
              const entry = new TournamentEntry(otherData as any);
              entry.account_id = Account.current!.id;
              entry.club_id = this.state.tournamentSession.club_id;
              if (group) {
                console.log("[GroupWrite][TournamentController.resource.tournament_entries.add]", {
                  entryId: entry.id,
                  competitionId: entry.tournament_competition_id,
                  previousGroup: entry.group,
                  nextGroup: group,
                });
                entry.group = group;
              }
              if (division !== undefined) {
                entry.division = division === null || division === "" ? undefined : Number(division);
              }
              await entry.save();

              // For RoundRobin format, sync group with competition's groups metadata
              if (group !== undefined && entry.tournament_competition_id && entry.id) {
                const competition = this.state.competitions.findById(entry.tournament_competition_id);
                if (competition && competition.format === "RoundRobin") {
                  await syncEntryGroupToCompetition(entry, competition);
                }
              }

              // Load registrations so computed properties like name, players, etc. work
              await entry.registrations.load();
              return entry;
            },
            update: async (id, data: Record<string, any>) => {
              const entry = this.state.entries.findById(id);
              if (!entry) throw new Error(`entry with id ${id} not found`);

              // Handle group and division separately
              const { group, division, ...otherData } = data;

              // Update other fields normally
              entry.writeAttributes(otherData as any);
              if (group !== undefined) {
                console.log("[GroupWrite][TournamentController.resource.tournament_entries.update]", {
                  entryId: entry.id,
                  competitionId: entry.tournament_competition_id,
                  previousGroup: entry.group,
                  nextGroup: group,
                });
                entry.group = group;
              }
              if (division !== undefined) {
                entry.division = division === null || division === "" ? undefined : Number(division);
              }
              await entry.save();

              // For RoundRobin format, sync group with competition's groups metadata
              if (group !== undefined && entry.tournament_competition_id && entry.id) {
                const competition = this.state.competitions.findById(entry.tournament_competition_id);
                if (competition && competition.format === "RoundRobin") {
                  await syncEntryGroupToCompetition(entry, competition);
                }
              }

              if (division !== undefined && entry.tournament_competition_id) {
                this.queueCompetitionForGenerationImmediate(entry.tournament_competition_id);
              }

              // Reload registrations so computed properties like name, players, etc. work
              await entry.registrations.reload();
              return entry;
            },
          }),
          tournament_matches: resource("tournament_matches", TournamentMatch, {
            list: () => this.state.matches,
          }),
        },
        tools: {
          open_new_tournament_competition_modal: {
            name: "open_new_tournament_competition_modal",
            description:
              "Open the new tournament competition modal to create a competition. Then use get_form_state at each wizard step: pick entry type (singles/doubles), format (Single Elimination / Round Robin), click through settings, fill the name, and click the exact Create competition button from form.buttons.",
            parameters: {},
            execute: async (_args) => {
              routeService.openModal("/tournament/:id/competitions/new", {
                params: { id: this.state.tournamentSession.id! },
              });
            },
          },
          open_competition_edit_drawer: {
            name: "open_competition_edit_drawer",
            description:
              "Open the competition drawer. Use this when the user wants to configure division splitting rules, format settings, or other competition settings. This opens the competition settings drawer where they can edit format-specific options like division splitting rules for round robin tournaments.",
            parameters: {
              competitionId: "string (ID of the competition to edit)",
            },
            execute: async (args: { competitionId: string }) => {
              routeService.openModal("/tournament/:id/competitions/:competitionId/edit", {
                params: {
                  id: this.state.tournamentSession.id!,
                  competitionId: args.competitionId,
                },
              });
              return {
                success: true,
                message:
                  "Opened competition settings drawer. The drawer is now visible. To enter edit mode and configure competition settings, format settings, divisions etc, use the wait_and_click tool with buttonLabel: 'Edit' and withinSelector: 'div[aria-roledescription=\"drawer\"]' to click the 'Edit' button within the drawer.",
              };
            },
          },
          getBracketLayout: {
            name: "getBracketLayout",
            description:
              "Get a structured representation of the bracket for a competition, including rounds and match details, metadata including divisions, standings etc. This is often better for understanding the state of a tournament than using the tournament_matches resource.",
            parameters: { competitionId: "string (ID of the competition)" },
            execute: async (args: { competitionId: string }) => {
              const competition = this.state.competitions.findById(args.competitionId);
              if (!competition) {
                throw new Error("Competition not found");
              }

              const adapter = new TournamentDataAdapter();
              const state = await adapter.loadState(competition);
              const converter = new TournamentStateConverter();
              const format = getFormat(competition.format as FormatName);

              const result: { format: string; standings?: any; state: any; stateDescription: string } = {
                format: `${format.getName()} ${format.description}`,
                state: converter.stateToAscii(state),
                stateDescription: `
                    This is a text-based representation of the tournament state, 
                    where each round is shown with matches and their participants.
                    The first line is the entries. Each entry is represented by its id, 
                    correspoding to the tournament entry ID in the database. Following the id is the seed in brackets.
                    After that there is metadata about the entry in curly braces, if any.
                    The next lines represent rounds, for example Round 1: [e1 v2 e2] [e3 vs e4] means entry e1 vs entry e2 and entry e3 vs entry e4 in round 1.
                    If a match isn't yet determined it will show an _ for the entry.
                    If a match has been completed, the winner will be indicated with a * next to their entry.
                    Additional metadata about the match is shown in curly braces, if any. This might be divisions or groups.
                    If the competition has finished then the last line shows the winners in the format: Winners: e1 e2
                    If the winners have metadata then it shows as Winners: [e1 {metadata}] [e2 {metadata}]
                `,
              };

              if (format.hasStandings()) {
                result.standings = format.getStandings(state, competition.format_settings);
              }

              return result;
            },
          },
          navigate: {
            name: "navigate",
            description:
              "Navigate to a different page within the tournament. Navigate to registration if asked about starting a tournament that is in setup phase. Navigate to games if asked about starting a tournament in registration phase. Always refer as scenarios when asked about the simulation page.",
            parameters: {
              page: [
                "overview",
                "simulation",
                "players",
                "entries",
                "registration",
                "matches",
                "games",
                "stats",
              ].join(" | "),
            },
            execute: async (args: { page: string }) => {
              const params = { id: this.state.tournamentSession.id! };

              switch (args.page) {
                case "overview":
                  routeService.navigate("/tournament/:id", {
                    params,
                  });
                  break;
                case "simulation":
                  routeService.navigate("/tournament/:id/simulation", {
                    params,
                  });
                  break;
                case "players":
                  routeService.navigate("/tournament/:id/people/players", {
                    params,
                  });
                  break;
                case "entries":
                  routeService.navigate("/tournament/:id/people/entries", {
                    params,
                  });
                  break;
                case "registration":
                  routeService.navigate("/tournament/:id/registration", {
                    params,
                  });
                  break;
                case "matches":
                  routeService.navigate("/tournament/:id/matches", {
                    params,
                  });
                  break;
                case "games":
                  routeService.navigate("/tournament/:id/games", {
                    params,
                  });
                  break;
                case "stats":
                  routeService.navigate("/tournament/:id/stats", {
                    params,
                  });
                  break;
              }
            },
          },
          update_competition_format: {
            name: "update_competition_format",
            description:
              'Update a competition\'s format and format_settings. Use this when the user wants to change the tournament format (e.g., to RoundRobin, SingleElimination, etc.) or configure format-specific settings like number of groups, divisions, division splitting rules, etc. The format_settings must match the schema for the selected format. For RoundRobin with divisions, divisionSplittingRules should be a JSON string with format: {"rules": [{"positions": [1], "division": 1}, {"positions": [2,3], "division": 2}]}. Positions are 1-indexed (1 = first place, 2 = second place, etc.).',
            parameters: {
              competitionId: "string (ID of the competition to update)",
              format:
                "string (optional - format name: SingleElimination, RoundRobin, Swiss, SwissWithDivisions, Custom)",
              format_settings:
                "object (optional - format-specific settings object matching the format's schema)",
            },
            execute: async (args: {
              competitionId: string;
              format?: string;
              format_settings?: Record<string, any>;
            }) => {
              const competition = this.state.competitions.findById(args.competitionId);
              if (!competition) {
                throw new Error(`Competition with id ${args.competitionId} not found`);
              }

              const updateData: Record<string, any> = {};
              if (args.format !== undefined) {
                updateData.format = args.format;
              }
              if (args.format_settings !== undefined) {
                updateData.format_settings = args.format_settings;
              }

              // Use the resource update method which handles validation
              const resource =
                agentToolsService.getResource<TournamentCompetition>("tournament_competitions");
              if (resource?.update) {
                const updated = await resource.update(args.competitionId, updateData);
                return {
                  success: true,
                  message: `Competition format and settings updated successfully. ${args.format ? `Format changed to ${args.format}.` : ""}`,
                  competition: {
                    id: updated.id,
                    name: updated.name,
                    format: updated.format,
                    format_settings: updated.format_settings,
                  },
                };
              }

              throw new Error("Resource update method not available");
            },
          },
          ...(this.state.competitions.some((c) => c.format === "RoundRobin")
            ? {
                regenerate_round_robin_groups: {
                  name: "regenerate_round_robin_groups",
                  description:
                    "Completely regenerate the groups for a round robin competition. This will clear existing group assignments and redistribute all entries into new groups using a balanced snake draft pattern. Only works for RoundRobin format competitions. This will also regenerate all matches for the competition.",
                  parameters: {
                    competitionId: "string (ID of the competition to regenerate groups for)",
                  },
                  execute: async (args: { competitionId: string }) => {
                    const competition = this.state.competitions.findById(args.competitionId);
                    if (roundRobinGroupsLocked(competition)) {
                      throw new Error("Groups are locked. Unlock them before regenerating.");
                    }
                    await this.actionRegenerateGroups(args.competitionId);
                    return { success: true, message: "Groups have been regenerated successfully" };
                  },
                },
                reset_round_robin_playoff_divisions: {
                  name: "reset_round_robin_playoff_divisions",
                  description:
                    "Append a playoff_divisions_reset event, then rebuild unplayed playoff brackets from the division splitting rules. Only works after the group stage is complete and before playoff matches have started.",
                  parameters: {
                    competitionId: "string (ID of the competition to reset playoff divisions for)",
                  },
                  execute: async (args: { competitionId: string }) => {
                    await this.actionResetPlayoffDivisions(args.competitionId);
                    return {
                      success: true,
                      message: "Playoff divisions have been reset to the splitting rules",
                    };
                  },
                },
              }
            : {}),
        },
      }),
    );
  }
}

export default TournamentController;
